import { useState } from "react";
import { Facebook, Sparkles } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { FinalCta } from "../components/FinalCta";
import { WORK_ITEMS, AFTER_PHOTOS, BUSINESS } from "../content";
import { track } from "../lib/track";

const CATEGORIES = ["All", "Bathrooms", "Kitchens"];

export const OurWorkPage = () => {
  const [cat, setCat] = useState("All");
  const items = cat === "All" ? WORK_ITEMS : WORK_ITEMS.filter((w) => w.category === cat);

  return (
    <>
      <Seo
        title="Our Work — Real Before & After Results | Pure Bliss Clean"
        description={`See the Pure Bliss difference. Genuine before-and-after cleaning results from homes across ${BUSINESS.region} — bathrooms, kitchens, appliances and deep cleans.`}
        path="/our-work"
      />
      <PageHero
        eyebrow="Our Work"
        titleLines={["See The", "Pure Bliss Difference"]}
        intro={`Our work speaks for itself. Explore genuine before-and-after results from homes we've cleaned throughout ${BUSINESS.region} — drag each slider to compare.`}
      />

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <Reveal>
          <div data-testid="work-category-tabs" className="flex flex-wrap gap-3" role="tablist" aria-label="Filter work by category">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={cat === c}
                data-testid={`work-tab-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-200 ${
                  cat === c ? "bg-brand-teal text-white shadow-md" : "border border-slate-300 bg-white text-brand-ink hover:border-brand-teal hover:text-brand-teal"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-12">
          {items.map((w, i) => (
            <Reveal key={w.id} delay={0.08 * i}>
              <BeforeAfterSlider {...w} />
            </Reveal>
          ))}
        </div>
        <div className="mt-24">
          <Reveal>
            <h2 className="font-display text-2xl font-black text-brand-ink sm:text-3xl">Fresh From Recent Cleans</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600">
              Genuine finished results from homes we've cleaned across {BUSINESS.region} — every photo is real
              Pure Bliss Clean work.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {AFTER_PHOTOS.map((p, i) => (
              <Reveal key={p.src} delay={0.08 * i}>
                <figure data-testid={`after-photo-${i}`} className="group">
                  <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
                    <img
                      src={p.src}
                      alt={p.alt}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-3 flex items-baseline justify-between px-1">
                    <span className="font-display text-sm font-bold text-brand-ink">{p.title}</span>
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-teal">{p.category}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-col items-center gap-5 rounded-3xl border border-dashed border-teal-300 bg-teal-50/60 p-10 text-center">
            <Sparkles className="h-7 w-7 text-brand-teal" />
            <p className="max-w-md text-sm leading-relaxed text-slate-600">
              Every photo on this page is genuine Pure Bliss Clean work — no stock before &amp; after photos.
              New transformations are added regularly.
            </p>
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="our-work-facebook-link"
              onClick={() => track("social_click", { network: "facebook", location: "our_work" })}
              className="inline-flex items-center gap-2 rounded-full border border-brand-teal px-6 py-3 text-sm font-bold text-brand-teal transition-colors hover:bg-brand-teal hover:text-white"
            >
              <Facebook className="h-4 w-4" /> See the latest on Facebook
            </a>
          </div>
        </Reveal>
      </section>
      <FinalCta />
    </>
  );
};
